import type { Me, PreviewStatus } from '@myrewards/contracts';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Outlet } from 'react-router';
import { leaveTo } from '../session/leave';
import { homeHref } from '../session/presenter';
import { draftAssignments } from './rules';

// Only testers named in Admin › Preview & testing ever load these, so they're their own chunks.
const SamplePage = lazy(async () => ({ default: (await import('./SamplePage')).SamplePage }));
const PreviewPanel = lazy(async () => ({ default: (await import('./PreviewPanel')).PreviewPanel }));

/**
 * The page inside the shell: the route's own page, or while previewing its sample layout. Route
 * loaders fetch nothing in a preview (`previewAware`), and the route's component isn't rendered,
 * so a preview can't show a live record (APP-030, APP-059).
 */
export function PreviewOutlet({ previewing }: { previewing: boolean }): ReactElement {
  if (!previewing) return <Outlet />;
  return (
    <Suspense fallback={null}>
      <SamplePage />
    </Suspense>
  );
}

/**
 * Where a started preview lands when the card picked has no page of its own (APP-232 D5): its
 * Home, which for a previewed Payroll is the Payroll queue.
 */
export function previewHome(status: PreviewStatus): string {
  const preview = status.preview;
  if (preview === null) return '/';
  return homeHref({ roles: draftAssignments(preview.roles, preview.sector) });
}

export interface PreviewAs {
  /** Opens the panel; undefined when this person may not preview. */
  open: ((returnFocusTo: HTMLElement | null) => void) | undefined;
  /** The panel, to render once inside the shell's providers. */
  panel: ReactElement | null;
}

/**
 * Preview as for the signed-in shell (TEST1): opened from the avatar menu or the banner's Change.
 * Focus goes back to whichever opened it, even though the avatar menu has closed by then. Starting
 * a preview is a full page load of its Home, so nothing fetched before it stays on screen.
 */
export function usePreviewAs(
  me: Pick<Me, 'canPreview' | 'preview' | 'actingAs'> & { person: Pick<Me['person'], 'id'> },
): PreviewAs {
  const [isOpen, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const returnTo = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const target = returnTo.current;
      // After React Aria's own restore, which aims at the avatar menu item that has since closed.
      requestAnimationFrame(() => {
        if (target?.isConnected === true) target.focus();
      });
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  const open = useCallback((returnFocusTo: HTMLElement | null) => {
    returnTo.current = returnFocusTo;
    setLoaded(true);
    setOpen(true);
  }, []);

  if (!me.canPreview) return { open: undefined, panel: null };
  return {
    open,
    panel: loaded ? (
      <Suspense fallback={null}>
        <PreviewPanel
          isOpen={isOpen}
          onOpenChange={setOpen}
          current={me.preview}
          acting={me.actingAs == null ? null : { personId: me.person.id }}
          onStarted={(status, landing) => {
            leaveTo(landing ?? previewHome(status));
          }}
        />
      </Suspense>
    ) : null,
  };
}
